// ── 로그 관리 — 필터 폼 + 활성 필터 칩 컴포넌트 ─────────────────────────────
import React from 'react';
import { FEATURE_PRESETS, METHOD_OPTIONS, STATUS_GROUPS } from './constants';
import {
  filterFormStyle, filterRowStyle, filterLabelStyle, filterInputStyle,
  btnPrimaryStyle, btnSecondaryStyle, infoBarStyle, chipStyle,
} from './styles';
import { fmtNum } from './formatters';

/**
 * 로그 검색 필터 폼 + 활성 필터 칩 요약 바
 * @param {object}   filters        — 현재 필터 상태 (filterActor, filterFeature, ...)
 * @param {Function} onFilterChange — 개별 필터 상태 변경 콜백 (key, value) => void
 * @param {boolean}  loading        — 조회 중 여부 (검색 버튼 비활성)
 * @param {Function} onSearch       — 검색 실행 콜백 (FormEvent) => void
 * @param {Function} onReset        — 필터 초기화 콜백 () => void
 * @param {number}   total          — 전체 로그 건수
 * @param {number}   page           — 현재 페이지
 * @param {number}   totalPages     — 전체 페이지 수
 */
export function LogFilterForm({
  filters, onFilterChange, loading, onSearch, onReset, total, page, totalPages,
}) {
  const {
    filterActor, filterFeature, filterMethod, filterStatus,
    filterDateFrom, filterDateTo, filterBodySearch,
  } = filters;

  // 선택된 기능 프리셋 라벨
  const selectedFeatureLabel = FEATURE_PRESETS.find((f) => f.value === filterFeature)?.label || '';

  return (
    <>
      {/* ── 필터 폼 ── */}
      <form style={filterFormStyle} onSubmit={onSearch}>
        <div style={filterRowStyle}>

          {/* 이름/ID 텍스트 검색 */}
          <label style={filterLabelStyle}>
            <span>이름 / 아이디 검색</span>
            <input
              style={{ ...filterInputStyle, minWidth: 150 }}
              placeholder="직원 이름 또는 ID 입력"
              value={filterActor}
              onChange={(e) => onFilterChange('filterActor', e.target.value)}
            />
          </label>

          {/* 내용 검색: request_body 키워드 */}
          <label style={filterLabelStyle}>
            <span>내용 검색</span>
            <input
              style={{ ...filterInputStyle, minWidth: 160 }}
              placeholder="상호명, 마케터명 등 키워드"
              value={filterBodySearch}
              onChange={(e) => onFilterChange('filterBodySearch', e.target.value)}
            />
          </label>

          {/* 기능별 프리셋 드롭다운 */}
          <label style={filterLabelStyle}>
            <span>기능</span>
            <select
              style={{ ...filterInputStyle, minWidth: 160 }}
              value={filterFeature}
              onChange={(e) => onFilterChange('filterFeature', e.target.value)}
            >
              {FEATURE_PRESETS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </label>

          {/* 날짜 범위: 시작일 */}
          <label style={filterLabelStyle}>
            <span>시작일</span>
            <input
              style={filterInputStyle}
              type="date"
              value={filterDateFrom}
              onChange={(e) => onFilterChange('filterDateFrom', e.target.value)}
            />
          </label>

          {/* 날짜 범위: 종료일 */}
          <label style={filterLabelStyle}>
            <span>종료일</span>
            <input
              style={filterInputStyle}
              type="date"
              value={filterDateTo}
              onChange={(e) => onFilterChange('filterDateTo', e.target.value)}
            />
          </label>

          {/* 메서드 드롭다운 */}
          <label style={filterLabelStyle}>
            <span>메서드</span>
            <select
              style={{ ...filterInputStyle, minWidth: 90 }}
              value={filterMethod}
              onChange={(e) => onFilterChange('filterMethod', e.target.value)}
            >
              {METHOD_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>

          {/* 상태코드 그룹 드롭다운 */}
          <label style={filterLabelStyle}>
            <span>상태코드</span>
            <select
              style={{ ...filterInputStyle, minWidth: 140 }}
              value={filterStatus}
              onChange={(e) => onFilterChange('filterStatus', e.target.value)}
            >
              {STATUS_GROUPS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>

          {/* 검색 / 초기화 버튼 */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <button type="submit" style={btnPrimaryStyle} disabled={loading}>
              {loading ? '조회 중...' : '검색'}
            </button>
            <button type="button" style={btnSecondaryStyle} onClick={onReset}>
              초기화
            </button>
          </div>
        </div>
      </form>

      {/* ── 활성 필터 요약 칩 바 ── */}
      <div style={infoBarStyle}>
        {filterActor && (
          <span style={chipStyle('var(--std-badge-bg, rgba(59,154,242,0.12))', 'var(--std-badge-text, #1d7de6)')}>👤 {filterActor}</span>
        )}
        {selectedFeatureLabel && selectedFeatureLabel !== '전체 기능' && (
          <span style={chipStyle('#fefce8', '#a16207')}>🔖 {selectedFeatureLabel}</span>
        )}
        {filterDateFrom && (
          <span style={chipStyle('#f0fdf4', '#15803d')}>
            📅 {filterDateFrom}{filterDateTo ? ` ~ ${filterDateTo}` : ''}
          </span>
        )}
        {filterBodySearch && (
          <span style={chipStyle('#f0fdfa', '#0f766e')}>🔍 "{filterBodySearch}"</span>
        )}
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          총 <strong>{fmtNum(total)}</strong>건 · {page}/{totalPages} 페이지
        </span>
      </div>
    </>
  );
}
